import { useEffect } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useAuthenticatedApiClient } from "@/hooks/useAuthenticatedApiClient";
import FinancialApplicationApiClient from "@/api/financialApplicationApiClient";

import { financialApplicationAtomFamily } from "@/atoms/financialApplicationAtomFamily";

/**
 * Interface for conditional flags used across financial application components
 */
export interface FinancialApplicationConditionalFlags {
  // Show account details if any products are selected
  isAccountDetailsEnabled: boolean;

  // Show joint account owners if user selected "will have joint owners" AND has personal products selected
  isJointAccountOwnersEnabled: boolean;
  
  // Show personal account beneficiaries if user selected "will have personal account beneficiaries" AND has personal products selected
  isPersonalAccountBeneficiariesEnabled: boolean;
  
  // Show organization info if business or IOLTA products are selected
  isOrganizationInfoEnabled: boolean;
  
  // Show key people if business or IOLTA products are selected
  isKeyPeopleEnabled: boolean;
  
  // Show personal acceptable use if personal products are selected
  isPersonalAccountAcceptableUseEnabled: boolean;
  
  // Show organization acceptable use if business or IOLTA products are selected
  isOrganizationAcceptableUseEnabled: boolean;
  
  // Risk review tab conditional flags
  // Show personal account risk review tab if personal products are selected
  isPersonalAccountRiskReviewEnabled: boolean;
  
  // Show law firm organization account risk review tab if business/IOLTA products are selected AND has Offices of Lawyers NAICS code
  isLawFirmOrganizationAccountRiskReviewEnabled: boolean;
  
  // Show non-law firm organization account risk review tab if business/IOLTA products are selected but NOT Offices of Lawyers NAICS code
  isNonLawFirmOrganizationAccountRiskReviewEnabled: boolean;
  
  // Show attorney bar number if IOLTA products are selected
  shouldShowAttorneyBarNumber: boolean;
  
  // Show business name on debit card if business or IOLTA products are selected
  showBusinessCard: boolean;
}

/**
 * Hook to manage the financial application core data state
 * @param financialApplicationId - The ID of the financial application
 * @returns An object containing the core data, refresh function, loading state, and computed conditional flags
 */
export const useFinancialApplicationCoreData = (financialApplicationId: string, refreshOnInit: boolean = true) => {
  const financialApplicationApiClient = useAuthenticatedApiClient(FinancialApplicationApiClient);

  const financialApplicationAtomValue = useAtomValue(
    financialApplicationAtomFamily(financialApplicationId)
  );

  const [coreData] = useAtom(financialApplicationAtomValue.coreData);
  const [coreDataLoading] = useAtom(financialApplicationAtomValue.coreDataLoading);
  const [coreDataError] = useAtom(financialApplicationAtomValue.coreDataError);
  const [coreDataFetched] = useAtom(financialApplicationAtomValue.coreDataFetched);
  const [disclosures] = useAtom(financialApplicationAtomValue.disclosures);
  const [conditionalFlags] = useAtom(financialApplicationAtomValue.conditionalFlags);
  const refreshCoreDataAtom = useSetAtom(financialApplicationAtomValue.refreshCoreData);

  const refreshCoreData = async () => {
    await refreshCoreDataAtom(financialApplicationApiClient, true);
  };

  useEffect(() => {
    if (financialApplicationId && refreshOnInit && !coreDataFetched) {
      refreshCoreDataAtom(financialApplicationApiClient, false);
    }
  }, [financialApplicationId, refreshOnInit, coreDataFetched, refreshCoreDataAtom, financialApplicationApiClient]);

  return {
    coreData,
    coreDataError,
    refreshCoreData,
    disclosures,
    coreDataLoading,
    conditionalFlags,
  };
};
